'use client'

import { useMutation, useQuery } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { useEffect, useMemo } from 'react'

import { findReasoning, parseBrief, type ParsedBrief } from '@/features/home/lib/home-brief-prompt'
import {
  getResearchReport,
  parseResearchApiError,
  postResearchQuestion,
} from '@/service/research.api'
import { homeAiBriefAtom } from '@/state/home'

const POLL_MS = 4000
/** A job that hasn't delivered by now is treated as lost (the assistant's own jobs take ~1 min). */
const GIVE_UP_MS = 10 * 60_000

export interface HomeAiBriefState {
  status: 'idle' | 'running' | 'done' | 'failed'
  parsed: ParsedBrief | null
  error: string | null
  requestedAt: number | null
  finishedAt: number | null
  /** Tools the research run has called so far, newest last (e.g. "funding_rates"). */
  steps: string[]
  /** What the delivered brief cited. */
  sources: string[]
  /** The AI's reasoning for one idea, when the brief covered it. */
  reasoningFor: (idea: { id: string; symbol: string }) => string | null
  /** The AI's one line about a datapoint card, when the brief covered it. */
  noteFor: (datapointId: string) => string | null
  start: (prompt: string, ideaIds: string[], datapointIds?: string[]) => void
}

/**
 * Runs the daily brief as a research job: the prompt goes to the research assistant, the job is
 * polled every few seconds, and the delivered markdown is stored (per user, across visits) and
 * split into the brief and each idea's reasoning. Only starts when asked, since a run spends the
 * user's research credits.
 */
export function useHomeAiBrief(
  /** The user's symbols and coins, to pick the brief's sentences out of a long report. */
  terms: readonly string[] = []
): HomeAiBriefState {
  const [brief, setBrief] = useAtom(homeAiBriefAtom)

  const startMutation = useMutation({
    mutationFn: ({ prompt }: { prompt: string; ideaIds: string[]; datapointIds: string[] }) =>
      postResearchQuestion(prompt),
    onMutate: ({ ideaIds, datapointIds }) =>
      setBrief({ jobId: null, status: 'running', requestedAt: Date.now(), ideaIds, datapointIds }),
    onSuccess: ({ jobId }) => setBrief((current) => current && { ...current, jobId }),
    onError: (error) =>
      setBrief({
        jobId: null,
        status: 'failed',
        requestedAt: Date.now(),
        error: parseResearchApiError(error).message,
      }),
  })

  const jobId = brief?.status === 'running' ? brief.jobId : null
  const poll = useQuery({
    queryKey: ['home-ai-brief', jobId],
    queryFn: () => getResearchReport(jobId!),
    enabled: jobId !== null,
    refetchInterval: POLL_MS,
    retry: 2,
  })

  useEffect(() => {
    const result = poll.data
    if (!result || !brief || brief.status !== 'running' || brief.jobId !== result.jobId) return
    if (result.phase === 'DELIVERED') {
      const markdown = result.report.blocks
        .flatMap((block) => (block.kind === 'markdown' ? [block.content] : []))
        .join('\n\n')
      const sources = [
        ...new Set(
          result.report.citations
            .map((citation) => citation.sourceTable || citation.tool)
            .filter(Boolean)
        ),
      ].slice(0, 6)
      setBrief({ ...brief, status: 'done', markdown, sources, finishedAt: Date.now() })
    } else if (
      result.phase === 'FAILED' ||
      result.phase === 'KILLED' ||
      result.phase === 'CANCELLED'
    ) {
      setBrief({ ...brief, status: 'failed', error: result.error ?? result.phase })
    }
  }, [poll.data, brief, setBrief])

  useEffect(() => {
    if (!poll.error || !brief || brief.status !== 'running') return
    setBrief({ ...brief, status: 'failed', error: parseResearchApiError(poll.error).message })
  }, [poll.error, brief, setBrief])

  // A run left "running" by a closed tab, or one that never delivers, shouldn't spin forever.
  useEffect(() => {
    if (!brief || brief.status !== 'running') return
    const remaining = brief.requestedAt + GIVE_UP_MS - Date.now()
    const timer = setTimeout(
      () => setBrief((current) => current && { ...current, status: 'failed', error: 'timeout' }),
      Math.max(0, remaining)
    )
    return () => clearTimeout(timer)
  }, [brief, setBrief])

  const termsKey = terms.join('|')
  const parsed = useMemo(
    () => (brief?.status === 'done' && brief.markdown ? parseBrief(brief.markdown, terms) : null),
    // `terms` is rebuilt each render; its content is termsKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [brief, termsKey]
  )
  const steps = useMemo(
    () =>
      [...(poll.data?.events ?? [])]
        .sort((a, b) => a.seq - b.seq)
        .flatMap((event) => (event.tool ? [event.tool] : [])),
    [poll.data]
  )

  return {
    status: brief?.status ?? 'idle',
    parsed,
    error: brief?.status === 'failed' ? (brief.error ?? null) : null,
    requestedAt: brief?.requestedAt ?? null,
    finishedAt: brief?.finishedAt ?? null,
    steps,
    sources: brief?.status === 'done' ? (brief.sources ?? []) : [],
    reasoningFor: (idea) => {
      if (!parsed) return null
      const index = brief?.ideaIds?.indexOf(idea.id) ?? -1
      if (parsed.structured) return index >= 0 ? (parsed.ideas[index + 1] ?? null) : null
      // A report that ignored the headings: the paragraph about this instrument, if any.
      return findReasoning(parsed.full, [idea.symbol])
    },
    noteFor: (datapointId) => {
      if (!parsed) return null
      const index = brief?.datapointIds?.indexOf(datapointId) ?? -1
      return index >= 0 ? (parsed.cardNotes[index + 1] ?? null) : null
    },
    start: (prompt: string, ideaIds: string[], datapointIds: string[] = []) =>
      startMutation.mutate({ prompt, ideaIds, datapointIds }),
  }
}
